import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Link,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  CalendarToday,
  People,
  Assessment,
} from '@mui/icons-material';
import * as api from '../services/api';

const CapacityPlanningV2Page: React.FC = () => {
  const navigate = useNavigate();
  const [anno, setAnno] = useState<number>(new Date().getFullYear());
  const [strutture, setStrutture] = useState<api.Struttura[]>([]);
  const [selectedStrutture, setSelectedStrutture] = useState<string[]>([]);
  const [data, setData] = useState<api.CapacityPlanningV2Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mesiNomi = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => {
    loadStrutture();
  }, []);

  useEffect(() => {
    if (strutture.length > 0) {
      loadData();
    }
  }, [anno, selectedStrutture]);

  const loadStrutture = async () => {
    try {
      const result = await api.getStrutture();
      setStrutture(result);
      // Default: tutte selezionate
      setSelectedStrutture(result.map(s => s.nome));
    } catch (err: any) {
      console.error('Errore caricamento strutture:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const struttureFilter = selectedStrutture.length === strutture.length ? undefined : selectedStrutture;
      const result = await api.getCapacityPlanningV2(anno, struttureFilter);
      setData(result);
    } catch (err: any) {
      console.error('Errore caricamento capacity planning:', err);
      setError(err.response?.data?.error || 'Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleStruttureChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedStrutture(typeof value === 'string' ? value.split(',') : value);
  };

  const handleMeseClick = (mese: number) => {
    const struttureParam = selectedStrutture.length === strutture.length
      ? ''
      : `?strutture=${selectedStrutture.join(',')}`;
    navigate(`/reportistica/capacity-planning-v2/${anno}/${mese}${struttureParam}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ok': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'ok': return <CheckCircle />;
      case 'warning': return <Warning />;
      case 'critical': return <TrendingDown />;
      default: return undefined;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ok': return 'OK';
      case 'warning': return 'Attenzione';
      case 'critical': return 'Critico';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const { summary, monthly_data } = data;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Capacity Planning 2
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Strutture</InputLabel>
            <Select
              multiple
              value={selectedStrutture}
              onChange={handleStruttureChange}
              input={<OutlinedInput label="Strutture" />}
              renderValue={(selected) =>
                selected.length === strutture.length
                  ? 'Tutte le strutture'
                  : `${selected.length} selezionate`
              }
            >
              {strutture.map((struttura) => (
                <MenuItem key={struttura.nome} value={struttura.nome}>
                  {struttura.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            value={anno.toString()}
            exclusive
            onChange={(_, value) => value && setAnno(parseInt(value))}
            size="small"
          >
            <ToggleButton value={(anno - 1).toString()}>{anno - 1}</ToggleButton>
            <ToggleButton value={anno.toString()}>{anno}</ToggleButton>
            <ToggleButton value={(anno + 1).toString()}>{anno + 1}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <People sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Risorse Totali
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {data.num_risorse}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Capacità: {data.capacita_totale_mensile} gg/mese
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarToday sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Demand Totale Anno
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {summary.total_demand.toLocaleString()} gg
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Effort richiesto dai progetti
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Assessment sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Supply Pianificato
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {summary.total_supply.toLocaleString()} gg
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Allocazioni effettive
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: summary.total_gap >= 0
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {summary.total_gap >= 0 ? <TrendingUp sx={{ color: 'white', mr: 1 }} /> : <TrendingDown sx={{ color: 'white', mr: 1 }} />}
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Gap Totale
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {summary.total_gap >= 0 ? '+' : ''}{summary.total_gap.toLocaleString()} gg
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {summary.total_gap < 0
                  ? `Serve ~${summary.fte_medio_mancante} FTE in più`
                  : `Capacità residua disponibile`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alert Mesi Critici */}
      {summary.mesi_critici > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<Warning />}>
          <strong>⚠️ Attenzione:</strong> Hai <strong>{summary.mesi_critici} mese/i critico/i</strong> con demand superiore al supply.
          È necessario intervenire con consulenti esterni, posticipare progetti o aumentare allocazioni.
        </Alert>
      )}

      {/* Tabella Mensile */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Analisi Mensile {anno}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confronto tra effort richiesto dai progetti (Demand) e risorse pianificate (Supply)
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Mese</strong></TableCell>
                <TableCell align="right"><strong>Capacità<br/>Disponibile</strong></TableCell>
                <TableCell align="right"><strong>Demand<br/>Teorico</strong></TableCell>
                <TableCell align="right"><strong>Supply<br/>Reale</strong></TableCell>
                <TableCell align="right"><strong>Gap</strong></TableCell>
                <TableCell align="right"><strong>Saturazione</strong></TableCell>
                <TableCell align="right"><strong>FTE<br/>Mancanti</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthly_data.map((month) => (
                <TableRow
                  key={month.mese}
                  sx={{
                    bgcolor: month.status === 'critical' ? 'error.lighter' : month.status === 'warning' ? 'warning.lighter' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleMeseClick(month.mese)}
                      sx={{ fontWeight: 'bold', cursor: 'pointer', textDecoration: 'none' }}
                    >
                      {mesiNomi[month.mese - 1]}
                    </Link>
                  </TableCell>
                  <TableCell align="right">{month.capacita_disponibile} gg</TableCell>
                  <TableCell align="right">{month.demand_teorico.toFixed(1)} gg</TableCell>
                  <TableCell align="right">{month.supply_reale.toFixed(1)} gg</TableCell>
                  <TableCell align="right">
                    <Typography
                      sx={{
                        color: month.gap < 0 ? 'error.main' : 'success.main',
                        fontWeight: month.gap < 0 ? 'bold' : 'normal'
                      }}
                    >
                      {month.gap >= 0 ? '+' : ''}{month.gap.toFixed(1)} gg
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{month.saturazione.toFixed(1)}%</TableCell>
                  <TableCell align="right">
                    {month.fte_mancanti > 0 && (
                      <Chip
                        label={`+${month.fte_mancanti} FTE`}
                        size="small"
                        color="error"
                        variant="filled"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={getStatusIcon(month.status)}
                      label={getStatusLabel(month.status)}
                      size="small"
                      color={getStatusColor(month.status)}
                      variant="filled"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer con legenda */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Legenda:</strong><br/>
          • <strong>Capacità Disponibile</strong>: giorni lavorativi totali del team ({data.num_risorse} risorse × {data.giorni_lavorativi_mese} gg)<br/>
          • <strong>Demand Teorico</strong>: effort richiesto dai progetti (budget distribuito linearmente sulla durata)<br/>
          • <strong>Supply Reale</strong>: giorni effettivamente allocati dalle risorse<br/>
          • <strong>Gap</strong>: differenza Supply - Demand (negativo = sottodimensionato, positivo = capacità residua)<br/>
          • <strong>FTE Mancanti</strong>: Full-Time Equivalent necessari per colmare il gap (1 FTE = {data.giorni_lavorativi_mese} gg/mese)
        </Typography>
      </Box>
    </Box>
  );
};

export default CapacityPlanningV2Page;
